import React, { useEffect, useState } from 'react'
import {
  useFocusable,
  FocusContext,
  setFocus,
} from '@noriginmedia/norigin-spatial-navigation'
import { declineVppa, acceptVppa } from '../../actions/contentActions'
import { triggerSentryError } from '../../../../shared/helpers'
import googleAnalytics4 from '../../lib/googleAnalytics.js'
import { useAppSelector } from '../../hooks/useStore'
import tts from '../../lib/TTS'

import FocusableButtonTS from '../FocusableButton/FocusableButtonTS'
import Modal from '../Modal/Modal'
import { useKeyDown } from '../../../../shared/providers/KeyDownProvider'

import type { VppaModalProps } from './types'

const VPPA_TEXTS = {
  CONFIRM: {
    TITLE: 'Please Confirm',
    DESCRIPTION_LINE_1:
      "To use your station's Passport benefit, we need you to confirm the following once every two years:",
    DESCRIPTION_LINE_2:
      'I agree that PBS and my PBS station may share my viewing activity with each other and their service providers.',
    ACTION_1: 'Confirm',
    ACTION_2: 'Decline',
  },
  DECLINE: {
    TITLE: 'Confirmation Needed',
    DESCRIPTION_LINE_1:
      "We're sorry, but if you do not confirm, you cannot access your station's Passport benefit.",
    DESCRIPTION_LINE_2:
      'If you want to cancel, you must stop and contact your station:',
    ACTION_1: 'Go Back',
    ACTION_2: 'Decline Anyway',
  },
}

function VppaModal({ onCloseModal }: VppaModalProps) {
  const [vppaTexts, setVppaTexts] = useState(VPPA_TEXTS['CONFIRM'])
  const [isUserTryingToDecline, setIsUserTryingToDecline] = useState(false)

  const { ref } = useFocusable()

  const memberships = useAppSelector((state) => state.user.memberships)
  const UID = useAppSelector((state) => state.device.UID)

  useEffect(() => {
    googleAnalytics4.sendPageViewEvent('VPPA Confirm 1')
    tts.stopRead()
    tts.pushTextToRead(
      `${VPPA_TEXTS.CONFIRM.TITLE}. ${VPPA_TEXTS.CONFIRM.DESCRIPTION_LINE_1}. ${VPPA_TEXTS.CONFIRM.DESCRIPTION_LINE_2}.`
    )
  }, [])

  useEffect(() => {
    setVppaTexts(VPPA_TEXTS[isUserTryingToDecline ? 'DECLINE' : 'CONFIRM'])
    setTimeout(() => setFocus('vppa-modal-first-button'))
  }, [isUserTryingToDecline])

  const handleFirstButtonFocus = () => {
    tts.stopRead()
    tts.addTextToRead(`Focused on ${vppaTexts.ACTION_1}`)
  }

  const handleSecondButtonFocus = () => {
    tts.stopRead()
    tts.addTextToRead(`Focused on ${vppaTexts.ACTION_2}`)
  }

  useKeyDown('back', () => onCloseModal({ accepted: false }), {
    stopPropagation: true,
  })

  const handleFirstButtonEnter = () => {
    if (!isUserTryingToDecline) {
      tts.pushTextToRead(`Selected ${vppaTexts.ACTION_1}`)
      acceptVppa(UID) //@TODO Move to a hook
        .then(() => {
          onCloseModal({ accepted: true })
        })
        .catch((err) => {
          triggerSentryError('acceptVppa', 'VppaModal.tsx', {
            errorMessage: err.message ?? 'Undefined error message (default)',
            errorLocation: 'VppaModal - Accept VPPA',
          })
        })
    } else {
      tts.pushTextToRead(
        `Selected ${vppaTexts.ACTION_1} ${VPPA_TEXTS.CONFIRM.TITLE}. ${VPPA_TEXTS.CONFIRM.DESCRIPTION_LINE_1}. ${VPPA_TEXTS.CONFIRM.DESCRIPTION_LINE_2}.`
      )
      setIsUserTryingToDecline(false)
    }
  }

  const handleSecondButtonEnter = () => {
    if (!isUserTryingToDecline) {
      tts.pushTextToRead(
        `Selected ${vppaTexts.ACTION_2} ${VPPA_TEXTS.DECLINE.TITLE}. ${VPPA_TEXTS.DECLINE.DESCRIPTION_LINE_1}. ${VPPA_TEXTS.DECLINE.DESCRIPTION_LINE_2}.`
      )
      setIsUserTryingToDecline(true)
      googleAnalytics4.sendPageViewEvent('VPPA Confirm 2')
    } else {
      declineVppa(UID)
        .then(() => {
          onCloseModal({ accepted: false, declined: true })
        })
        .catch((err) => {
          triggerSentryError('declineVppa', 'VppaModal.tsx', {
            errorMessage: err.message ?? 'Undefined error message (default)',
            errorLocation: 'VppaModal - Decline VPPA',
          })
        })
      tts.pushTextToRead(`Selected ${vppaTexts.ACTION_2}`)
    }
  }

  return (
    <FocusContext.Provider value={'vppa-modal'}>
      <div className="vppa-modal-container" ref={ref}>
        <Modal error={true}>
          <h1 className="vppa-modal-container__title">{vppaTexts.TITLE}</h1>
          <div className="vppa-modal-container__content">
            {isUserTryingToDecline ? (
              <>
                <p>{VPPA_TEXTS.DECLINE.DESCRIPTION_LINE_1}</p>
                <p>{VPPA_TEXTS.DECLINE.DESCRIPTION_LINE_2}</p>
                {memberships.map((x, i) => {
                  return (
                    <div key={i} className="station">
                      {x.title} Passport
                    </div>
                  )
                })}
              </>
            ) : (
              <>
                <p>{VPPA_TEXTS.CONFIRM.DESCRIPTION_LINE_1}</p>
                <p>{VPPA_TEXTS.CONFIRM.DESCRIPTION_LINE_2}</p>
              </>
            )}
          </div>
          <div className="vppa-modal-container__buttons">
            <FocusableButtonTS
              onEnterPress={handleFirstButtonEnter}
              className="vppa-modal-container__first-button"
              onFocus={handleFirstButtonFocus}
              focusKey="vppa-modal-first-button"
            >
              {vppaTexts.ACTION_1}
            </FocusableButtonTS>
            <FocusableButtonTS
              onEnterPress={handleSecondButtonEnter}
              className="vppa-modal-container__second-button"
              onFocus={handleSecondButtonFocus}
            >
              {vppaTexts.ACTION_2}
            </FocusableButtonTS>
          </div>
        </Modal>
      </div>
    </FocusContext.Provider>
  )
}

export default VppaModal
